/**
 * Centralized Error Monitoring Service
 *
 * Provides comprehensive error tracking, logging, and user-friendly error recovery
 */

class ErrorService {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Keep last 100 errors
    this.listeners = [];
    this.isProduction = import.meta.env.PROD;
  }

  /**
   * Log an error with context and user-friendly handling
   */
  logError(error, context = {}) {
    const errorInfo = {
      id: this.generateErrorId(),
      timestamp: new Date().toISOString(),
      message: error.message || "Unknown error",
      stack: error.stack,
      type: error.name || "Error",
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: context.userId,
        component: context.component,
        action: context.action,
        ...context,
      },
      severity: this.determineSeverity(error, context),
      handled: context.handled || false,
    };

    // Add to local storage
    this.errors.unshift(errorInfo);
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Persist to localStorage for debugging
    this.persistErrors();

    // Notify listeners
    this.notifyListeners(errorInfo);

    // Console logging in development
    if (!this.isProduction) {
    }

    // In production, send to monitoring service (e.g., Sentry)
    if (this.isProduction && errorInfo.severity >= 2) {
      this.sendToMonitoringService(errorInfo);
    }

    return errorInfo;
  }

  /**
   * Generate unique error ID
   */
  generateErrorId() {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Determine error severity (0=info, 1=warning, 2=error, 3=critical)
   */
  determineSeverity(error, context) {
    // Critical errors that break core functionality
    if (
      error.name === "ChunkLoadError" ||
      error.message.includes("Loading chunk") ||
      context.component === "Dialer" ||
      context.action === "call"
    ) {
      return 3; // Critical
    }

    // Network/API errors
    if (
      error.name === "NetworkError" ||
      error.message.includes("fetch") ||
      context.type === "api"
    ) {
      return 2; // Error
    }

    // UI/Component errors
    if (error.name === "TypeError" || context.type === "component") {
      return 1; // Warning
    }

    return 2; // Default to error
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error, context = {}) {
    const errorType = error.name || "Error";
    const errorMessage = error.message || "Unknown error occurred";

    // Network errors
    if (errorMessage.includes("fetch") || errorMessage.includes("Network")) {
      return {
        title: "Connection Problem",
        message:
          "Unable to connect to the server. Please check your internet connection and try again.",
        action: "Retry",
        severity: "warning",
      };
    }

    // Authentication errors
    if (errorMessage.includes("401") || errorMessage.includes("Unauthorized")) {
      return {
        title: "Session Expired",
        message: "Your session has expired. Please log in again.",
        action: "Login",
        severity: "error",
      };
    }

    // Validation errors
    if (errorMessage.includes("validation") || context.type === "validation") {
      return {
        title: "Invalid Input",
        message: "Please check your input and try again.",
        action: "Fix",
        severity: "warning",
      };
    }

    // Call/Dialer errors
    if (context.component === "Dialer" || context.action === "call") {
      return {
        title: "Call Failed",
        message:
          "Unable to place the call. Please check your connection and try again.",
        action: "Retry Call",
        severity: "error",
      };
    }

    // Generic error
    return {
      title: "Something Went Wrong",
      message: "An unexpected error occurred. Our team has been notified.",
      action: "Retry",
      severity: "error",
    };
  }

  /**
   * Add error listener
   */
  addListener(listener) {
    this.listeners.push(listener);
  }

  /**
   * Remove error listener
   */
  removeListener(listener) {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  /**
   * Notify all listeners of new error
   */
  notifyListeners(errorInfo) {
    this.listeners.forEach((listener) => {
      try {
        listener(errorInfo);
      } catch (err) {}
    });
  }

  /**
   * Persist errors to localStorage
   */
  persistErrors() {
    try {
      const recentErrors = this.errors.slice(0, 10); // Keep only recent errors
      localStorage.setItem("nexus_errors", JSON.stringify(recentErrors));
    } catch (err) {}
  }

  /**
   * Load persisted errors
   */
  loadPersistedErrors() {
    try {
      const stored = localStorage.getItem("nexus_errors");
      if (stored) {
        this.errors = JSON.parse(stored);
      }
    } catch (err) {}
  }

  /**
   * Send error to monitoring service (placeholder for Sentry, etc.)
   */
  sendToMonitoringService(errorInfo) {
    // In a real app, this would send to Sentry, LogRocket, etc.
    // For now, we'll just log to console in production
  }

  /**
   * Get error statistics
   */
  getErrorStats() {
    const now = Date.now();
    const lastHour = now - 60 * 60 * 1000;
    const lastDay = now - 24 * 60 * 60 * 1000;

    const recentErrors = this.errors.filter(
      (e) => new Date(e.timestamp).getTime() > lastHour,
    );
    const dailyErrors = this.errors.filter(
      (e) => new Date(e.timestamp).getTime() > lastDay,
    );

    const severityCounts = this.errors.reduce((acc, error) => {
      acc[error.severity] = (acc[error.severity] || 0) + 1;
      return acc;
    }, {});

    return {
      total: this.errors.length,
      lastHour: recentErrors.length,
      lastDay: dailyErrors.length,
      severityCounts,
      mostCommon: this.getMostCommonErrors(),
    };
  }

  /**
   * Get most common error types
   */
  getMostCommonErrors() {
    const errorCounts = this.errors.reduce((acc, error) => {
      const key = `${error.type}: ${error.message}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(errorCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([error, count]) => ({ error, count }));
  }

  /**
   * Clear all errors
   */
  clearErrors() {
    this.errors = [];
    localStorage.removeItem("nexus_errors");
  }

  /**
   * Get all errors
   */
  getAllErrors() {
    return [...this.errors];
  }
}

// Global error service instance
const errorService = new ErrorService();

// Initialize error handling
errorService.loadPersistedErrors();

// Global error handlers
window.addEventListener("error", (event) => {
  errorService.logError(event.error, {
    type: "javascript",
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    handled: false,
  });
});

window.addEventListener("unhandledrejection", (event) => {
  errorService.logError(event.reason, {
    type: "promise_rejection",
    handled: false,
  });
});

export default errorService;
